import { documentsApi } from '../api/documents';
import { cryptoApi } from '../api/crypto';
import {
  clearRememberedPrivateKey,
  getPasskeyWrappedJwk,
  getRememberedPrivateKey,
  setPasskeyWrappedJwk,
  setRememberedPrivateKey,
} from './rememberedKeyStore';
import { requestE2eeUnlock } from './e2eeUnlockUi';
import {
  b64,
  decryptWithAesGcm,
  decryptPrivateJwkFromStorage,
  importPrivateJwk,
  unwrapDekBytesForUser,
  unwrapDekForUser,
  wrapDekBytesForLink,
} from './e2eeCrypto';
import { createPlatformPrfCredential, evalPrfSecret } from './webauthnPrf';

/**
 * Always-on E2EE client. After migration 0011 every uploaded file is
 * encrypted per-file (one DEK per file, AES-GCM ciphertext on disk),
 * and the per-user wrapped DEK + IV are streamed in the
 * `X-Edosar-Encryption` header by the download endpoints. The legacy
 * per-document `/e2ee` metadata + `/ciphertext` endpoints are gone.
 *
 * Public API kept (callers in `MemberDetail.tsx` and `DocumentView.tsx`
 * are unchanged): `decryptE2eeDocument(documentId)` and
 * `decryptE2eeDocumentSilently(documentId)` now resolve the document's
 * first file (these are used only for single-file documents) and call
 * the new `/api/documents/{id}/download` endpoint. Per-file decryption
 * is exposed via `decryptE2eeFile(documentId, fileId)` for grouped
 * documents (the view page iterates files when `file_count > 1`).
 */

let cachedPrivateKey: CryptoKey | null = null;

async function decryptJwkWithPrfRecord(rec: {
  credential_id_b64u: string;
  prf_salt_b64: string;
  jwk_ciphertext_b64: string;
  jwk_iv_b64: string;
}): Promise<JsonWebKey> {
  const salt = new Uint8Array(b64.b64ToBytes(rec.prf_salt_b64));
  const secret = new Uint8Array(await evalPrfSecret(rec.credential_id_b64u, salt));
  const secretAb = secret.buffer.slice(secret.byteOffset, secret.byteOffset + secret.byteLength);
  const aes = await window.crypto.subtle.importKey('raw', secretAb, { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
  const iv = new Uint8Array(b64.b64ToBytes(rec.jwk_iv_b64));
  const ciphertext = new Uint8Array(b64.b64ToBytes(rec.jwk_ciphertext_b64));
  const ciphertextAb = ciphertext.buffer.slice(ciphertext.byteOffset, ciphertext.byteOffset + ciphertext.byteLength);
  const pt = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, aes, ciphertextAb);
  return JSON.parse(new TextDecoder().decode(new Uint8Array(pt)));
}

async function getMyPrivateKey(): Promise<CryptoKey> {
  if (cachedPrivateKey) return cachedPrivateKey;
  const keys = await cryptoApi.getMyKeys();
  if (!keys) throw new Error('No encryption keys set for this account.');

  // Passkey-protected remembered key (preferred if present).
  const passkeyRec = await getPasskeyWrappedJwk();
  if (passkeyRec) {
    const jwk = await decryptJwkWithPrfRecord(passkeyRec);
    cachedPrivateKey = await importPrivateJwk(jwk, { extractable: false });
    return cachedPrivateKey;
  }

  // If user previously chose "remember on this device", load non-extractable key from IndexedDB.
  const remembered = await getRememberedPrivateKey();
  if (remembered) {
    cachedPrivateKey = remembered;
    return cachedPrivateKey;
  }

  const { passphrase, remember, usePasskey } = await requestE2eeUnlock({
    title: 'Deblochează criptarea',
    message: 'Introdu passphrase-ul tău E2EE ca să poți decripta documente.',
  });
  const jwk = await decryptPrivateJwkFromStorage(
    keys.encrypted_private_jwk,
    passphrase,
    keys.kdf_salt_b64,
    keys.kdf_iterations,
    keys.priv_enc_iv_b64
  );
  cachedPrivateKey = await importPrivateJwk(jwk, { extractable: false });

  try {
    if (remember) {
      if (usePasskey) {
        const { credentialIdB64u } = await createPlatformPrfCredential();
        const prfSalt = window.crypto.getRandomValues(new Uint8Array(32));
        const secret = new Uint8Array(await evalPrfSecret(credentialIdB64u, prfSalt));
        const secretAb = secret.buffer.slice(secret.byteOffset, secret.byteOffset + secret.byteLength);
        const aes = await window.crypto.subtle.importKey('raw', secretAb, { name: 'AES-GCM', length: 256 }, false, [
          'encrypt',
        ]);
        const iv = window.crypto.getRandomValues(new Uint8Array(12));
        const jwkBytes = new TextEncoder().encode(JSON.stringify(jwk));
        const ct = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aes, jwkBytes);
        await setPasskeyWrappedJwk({
          credential_id_b64u: credentialIdB64u,
          prf_salt_b64: b64.bytesToB64(prfSalt),
          jwk_iv_b64: b64.bytesToB64(iv),
          jwk_ciphertext_b64: b64.bytesToB64(new Uint8Array(ct)),
        });
        // Ensure we don't leave an unprotected remembered CryptoKey around.
        await clearRememberedPrivateKey();
      } else {
        await setRememberedPrivateKey(cachedPrivateKey);
      }
    }
  } catch {
    // ignore
  }

  return cachedPrivateKey;
}

export function clearCachedPrivateKey() {
  cachedPrivateKey = null;
}

export async function canDecryptE2eeSilently(): Promise<boolean> {
  // Only consider the non-passkey remembered CryptoKey as "silent".
  // WebAuthn PRF requires user interaction in many browsers.
  const remembered = await getRememberedPrivateKey();
  return Boolean(remembered);
}

/** Resolve the "primary" file for a document — the single file when
 *  `file_count === 1`, otherwise the first file by `file_index`. Used by
 *  the legacy single-document decrypt helpers (`decryptE2eeDocument`
 *  and `decryptE2eeDocumentSilently`) which exist to keep existing
 *  download/preview call sites working without leaking the per-file
 *  endpoint shape into them. */
async function resolvePrimaryFileId(documentId: number): Promise<{ fileId: number; filename: string; mime: string }> {
  const wrapper = await documentsApi.getDocument(documentId);
  const files = wrapper.document.files || [];
  if (files.length === 0) {
    throw new Error(`Document ${documentId} has no files attached.`);
  }
  const sorted = [...files].sort((a, b) => a.file_index - b.file_index);
  const first = sorted[0];
  return {
    fileId: first.id,
    filename: first.original_filename || `document-${documentId}`,
    mime: first.mime_type || 'application/octet-stream',
  };
}

async function decryptDownloadedFile(
  priv: CryptoKey,
  bundle: { ciphertext: ArrayBuffer; iv: string; wrapped_dek: string; filename: string | null },
  fallback: { filename: string; mime: string }
): Promise<{ bytes: Uint8Array; mime: string; filename: string }> {
  let dek: CryptoKey;
  try {
    dek = await unwrapDekForUser(priv, b64.b64ToBytes(bundle.wrapped_dek));
  } catch {
    throw new Error(
      'Cheia ta de criptare nu poate deschide acest document. Probabil a fost încărcat cu un alt cont sau ' +
        'cu chei rotite ulterior. Cere proprietarului familiei să reîmpacheteze cheile pentru contul tău.'
    );
  }
  const iv = b64.b64ToBytes(bundle.iv);
  try {
    const bytes = await decryptWithAesGcm(dek, new Uint8Array(bundle.ciphertext), iv);
    return {
      bytes,
      mime: fallback.mime,
      filename: bundle.filename || fallback.filename,
    };
  } catch {
    throw new Error(
      'Documentul criptat este corupt sau a fost modificat — verificarea integrității a eșuat. ' +
        'Încearcă să-l încarci din nou.'
    );
  }
}

/** Silent decrypt path — used by `DocumentView` when the user has a
 *  remembered (non-passkey) key, so the preview opens without
 *  prompting. Throws if the silent key is missing or the decryption
 *  fails — caller falls back to the interactive `decryptE2eeDocument`. */
export async function decryptE2eeDocumentSilently(
  documentId: number
): Promise<{ bytes: Uint8Array; mime: string; filename: string }> {
  const remembered = await getRememberedPrivateKey();
  if (!remembered) throw new Error('E2EE key not available for silent decrypt');
  const primary = await resolvePrimaryFileId(documentId);
  const bundle = await documentsApi.downloadEncryptedFile(documentId, primary.fileId);
  try {
    return await decryptDownloadedFile(remembered, bundle, { filename: primary.filename, mime: primary.mime });
  } catch (e: any) {
    // Map the user-facing strings back to opaque sentinels the caller already
    // pattern-matches on (kept for compat with `DocumentView` silent-fallback).
    const msg = String(e?.message || '');
    if (msg.startsWith('Cheia ta')) throw new Error('E2EE_ENVELOPE_MISMATCH');
    if (msg.startsWith('Documentul')) throw new Error('E2EE_CIPHERTEXT_CORRUPT');
    throw e;
  }
}

/** Interactive decrypt path — unlocks the private key (prompting if
 *  necessary), fetches the document's primary file ciphertext from
 *  `/api/documents/{id}/download`, and returns the decrypted bytes
 *  plus filename/mime. For grouped documents (`file_count > 1`),
 *  prefer `decryptE2eeFile(documentId, fileId)` which avoids the
 *  primary-file resolution. */
export async function decryptE2eeDocument(
  documentId: number
): Promise<{ bytes: Uint8Array; mime: string; filename: string }> {
  const primary = await resolvePrimaryFileId(documentId);
  const bundle = await documentsApi.downloadEncryptedFile(documentId, primary.fileId);
  const priv = await getMyPrivateKey();
  return decryptDownloadedFile(priv, bundle, { filename: primary.filename, mime: primary.mime });
}

/** Per-file decrypt for grouped documents. Hits
 *  `/api/documents/{documentId}/files/{fileId}/download` directly,
 *  skipping the "primary file" resolution. */
export async function decryptE2eeFile(
  documentId: number,
  fileId: number,
  fallback?: { filename?: string; mime?: string }
): Promise<{ bytes: Uint8Array; mime: string; filename: string }> {
  const bundle = await documentsApi.downloadEncryptedFile(documentId, fileId);
  const priv = await getMyPrivateKey();
  return decryptDownloadedFile(priv, bundle, {
    filename: fallback?.filename || `document-${documentId}-file-${fileId}`,
    mime: fallback?.mime || 'application/octet-stream',
  });
}

/** Silent per-file variant of `decryptE2eeFile` — uses the remembered
 *  key without prompting. Throws if the remembered key is missing or
 *  if decryption fails, mirroring `decryptE2eeDocumentSilently`'s
 *  contract (caller falls back to the interactive `decryptE2eeFile`).
 *
 *  Used by `DocumentView` to silently page through every file of a
 *  grouped document once the user has already unlocked the primary
 *  file. */
export async function decryptE2eeFileSilently(
  documentId: number,
  fileId: number,
  fallback?: { filename?: string; mime?: string }
): Promise<{ bytes: Uint8Array; mime: string; filename: string }> {
  const remembered = await getRememberedPrivateKey();
  if (!remembered) throw new Error('E2EE key not available for silent decrypt');
  const bundle = await documentsApi.downloadEncryptedFile(documentId, fileId);
  try {
    return await decryptDownloadedFile(remembered, bundle, {
      filename: fallback?.filename || `document-${documentId}-file-${fileId}`,
      mime: fallback?.mime || 'application/octet-stream',
    });
  } catch (e: any) {
    const msg = String(e?.message || '');
    if (msg.startsWith('Cheia ta')) throw new Error('E2EE_ENVELOPE_MISMATCH');
    if (msg.startsWith('Documentul')) throw new Error('E2EE_CIPHERTEXT_CORRUPT');
    throw e;
  }
}

/** Materialise per-file SHARE-LINK envelopes for an invite the owner
 *  just created. Called from `ShareInviteModal` immediately after
 *  `sharingApi.createMemberInvite` / `createFamilyInvite` resolves —
 *  that's the only moment the owner holds the raw token (the server
 *  thereafter only stores `token_hash`).
 *
 *  Per (document, file): unwrap the owner's DEK with their private
 *  key, AES-GCM-wrap it under the link key derived from the token,
 *  then upsert all per-file link envelopes for the document in one
 *  bulk request. Per-document failures are collected into `errors`
 *  rather than aborting the whole walk.
 *
 *  Counters:
 *   - `processed` counts files (not documents) that were successfully
 *     re-wrapped.
 *   - `skipped` counts files we couldn't process (no user envelope,
 *     unwrap failure, etc).
 */
export async function ensureLinkEnvelopesForMemberInvite(
  memberId: number,
  inviteToken: string
): Promise<{
  processed: number;
  skipped: number;
  errors: Array<{ document_id: number; error: string }>;
}> {
  const idsResp = await documentsApi.listMemberE2eeIds(memberId);
  const docs = idsResp.documents || [];
  if (!docs.length) return { processed: 0, skipped: 0, errors: [] };

  const priv = await getMyPrivateKey();
  let processed = 0;
  let skipped = 0;
  const errors: Array<{ document_id: number; error: string }> = [];

  for (const doc of docs) {
    try {
      const meta = await documentsApi.getE2eeMetadata(doc.id);
      const filesToUpsert: Array<{ file_id: number; wrapped_dek: string }> = [];
      for (const file of meta.files) {
        try {
          const dekBytes = await unwrapDekBytesForUser(
            priv,
            b64.b64ToBytes(file.wrapped_dek)
          );
          const link = await wrapDekBytesForLink(inviteToken, dekBytes);
          // Pack {iv ‖ ciphertext} as base64 so the link envelope can
          // travel as a single field. `unwrapDekBytesForLink` on the
          // recipient side splits the leading 12-byte IV back off.
          const packed = new Uint8Array(link.iv.length + link.ciphertext.length);
          packed.set(link.iv, 0);
          packed.set(link.ciphertext, link.iv.length);
          filesToUpsert.push({
            file_id: file.file_id,
            wrapped_dek: b64.bytesToB64(packed),
          });
        } catch {
          skipped++;
        }
      }
      if (filesToUpsert.length === 0) continue;
      await documentsApi.upsertE2eeLinkEnvelope(doc.id, {
        token: inviteToken,
        files: filesToUpsert,
      });
      processed += filesToUpsert.length;
    } catch (e: any) {
      const msg =
        String(e?.response?.data?.detail || e?.message || 'Unknown error').trim() ||
        'Unknown error';
      errors.push({ document_id: doc.id, error: msg });
    }
  }
  return { processed, skipped, errors };
}
